<template>
  <div class="farabi-result">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t("Farabi Result") }}</h4>
        <b-badge
          v-if="!loading && students.length"
          variant="light-success"
          class="ml-2"
        >
          {{ students.length }} {{ $t("Student(s)") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print Report") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-panel">
      <b-spinner variant="primary" />
      <h5 class="state-panel__title mt-2">{{ $t("Loading Report") }}</h5>
      <p class="state-panel__text">
        {{ $t("Please wait while we prepare the result.") }}
      </p>
    </div>

    <div v-else-if="error" class="state-panel state-panel--error">
      <feather-icon icon="AlertCircleIcon" size="36" class="text-danger" />
      <h5 class="state-panel__title text-danger mt-2">
        {{ $t("Unable to load report") }}
      </h5>
      <p class="state-panel__text">{{ error }}</p>
      <button class="btn-modern btn-print mt-2" @click="loadResult">
        <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
        <span>{{ $t("Retry") }}</span>
      </button>
    </div>

    <div v-else class="report-stack">
      <div
        v-for="(item, studentIndex) in students"
        :key="'farabi-' + (item.student.id || studentIndex)"
        class="report-sheet"
      >
        <div class="report-sheet__frame">
          <div class="report-sheet__body">
            <header class="letterhead">
              <img
                v-if="campus.logo"
                :src="campus.logo"
                alt="Logo"
                class="letterhead__logo"
              />
              <h1 class="letterhead__school">{{ campus.name }}</h1>
              <p v-if="campus.address" class="letterhead__address">
                {{ campus.address }}
              </p>
              <p v-if="examTitle" class="letterhead__exam">
                {{ examTitle }} EXAMINATION
              </p>
              <p class="letterhead__session">
                SESSION: {{ meta.sessionName }}
              </p>
            </header>

            <table class="student-info">
              <tbody>
                <tr>
                  <td class="student-info__details">
                    <table class="student-info__table">
                      <tbody>
                        <tr>
                          <td class="student-info__label">NAME:</td>
                          <td class="student-info__value">
                            {{ item.student.name }}
                          </td>
                        </tr>
                        <tr>
                          <td class="student-info__label">GR#:</td>
                          <td class="student-info__value">
                            {{ item.student.grNo }}
                          </td>
                        </tr>
                        <tr>
                          <td class="student-info__label">CLASS:</td>
                          <td class="student-info__value">
                            {{ classSectionDisplay }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                  <td class="student-info__photo">
                    <img
                      v-if="item.student.picture"
                      :src="item.student.picture"
                      :alt="item.student.name"
                      class="student-info__img"
                      loading="eager"
                      decoding="sync"
                    />
                    <div v-else class="student-info__placeholder">
                      <feather-icon icon="UserIcon" size="42" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <section class="report-block">
              <div class="report-block__title">
                {{ $t("Progress Report of") }} {{ examTitle }}
              </div>              <table class="marks-table">
                <thead>
                  <tr>
                    <th>{{ $t("Subjects") }}</th>
                    <th>{{ $t("Total Marks") }}</th>
                    <th>{{ $t("Obt. Marks") }}</th>
                    <th>{{ $t("Percentage %") }}</th>
                    <th>{{ $t("Grade") }}</th>
                    <th>{{ $t("Result") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(mark, markIndex) in item.marks"
                    :key="'mark-' + studentIndex + '-' + markIndex"
                  >
                    <td class="text-left">{{ mark.subjectName }}</td>
                    <td>{{ formatMark(mark.total) }}</td>
                    <td
                      class="obtained-cell"
                      :class="attendanceClass(mark.attendance)"
                      :title="attendanceLabel(mark.attendance)"
                    >
                      <span v-if="showStatusLetter(mark)">{{
                        mark.attendance
                      }}</span>
                      <span v-else>{{ formatMark(mark.obtained) }}</span>
                    </td>
                    <td>{{ formatPercent(mark.percentage) }}</td>
                    <td>{{ mark.grade || "-" }}</td>
                    <td
                      class="result-cell"
                    >
                      {{ mark.status || "-" }}
                    </td>
                  </tr>
                  <tr v-if="!item.marks.length">
                    <td colspan="6" class="text-center">
                      {{ $t("No marks found") }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section class="report-block report-block--result">
              <div class="report-block__title">
                {{ examTitle }} {{ $t("Result") }}
              </div>
              <table class="result-table">
                <thead>
                  <tr>
                    <th>{{ $t("Total Marks") }}</th>
                    <th>{{ $t("Obtained") }}</th>
                    <th>{{ $t("Percentage %") }}</th>
                    <th>{{ $t("Rank") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ formatMark(item.result.totalMarks) }}</td>
                    <td>{{ formatMark(item.result.obtainedMarks) }}</td>
                    <td>{{ formatPercent(item.result.percentage) }}</td>
                    <td>{{ item.result.rank || "-" }}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section class="report-block report-block--grades">
              <div class="report-block__title">{{ $t("Grade Keys") }}</div>
              <table class="grade-table">
                <thead>
                  <tr>
                    <th v-for="grade in gradeKeys" :key="'g-' + grade.grade">
                      {{ grade.grade }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td v-for="grade in gradeKeys" :key="'v-' + grade.grade">
                      {{ grade.range }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <footer class="signature">
              <div class="signature__line"></div>
              <div class="signature__label">
                {{ $t("Principal's Signature") }}
              </div>
            </footer>
          </div>
        </div>
      </div>

      <div v-if="!students.length" class="state-panel">
        <h5 class="font-weight-bold mb-1">
          {{ $t("No student records found") }}
        </h5>
        <p class="text-muted mb-0">
          {{ $t("Try different filters on the print result page.") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

const GRADE_KEYS = [
  { grade: "A*", range: "90+" },
  { grade: "A", range: "80+" },
  { grade: "B", range: "70+" },
  { grade: "C", range: "60+" },
  { grade: "NI", range: "50+" },
  { grade: "U", range: "50-" },
];

export default {
  name: "FarabiResult",
  components: {
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      students: [],
      campus: {
        name: "FARABI COACHING CENTRE",
        address: "",
        logo: null,
      },
      meta: {
        className: "",
        sectionName: "",
        sessionName: "",
        examTitle: "",
      },
      gradeKeys: GRADE_KEYS,
    };
  },
  computed: {
    examTitle() {
      return this.meta.examTitle || this.$route.query.examTypeName || "";
    },
    classSectionDisplay() {
      const cls = this.meta.className || this.$route.query.clsName || "";
      const sec = this.meta.sectionName || this.$route.query.secName || "";
      return [cls, sec].filter(Boolean).join(" - ") || "-";
    },
    requestedSIDs() {
      return String(this.$route.query.sIDs || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    },
    examTypeIDs() {
      return String(this.$route.query.examTypeID || this.$route.query.examID || "")
        .split(",")
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    },
  },
  created() {
    this.loadResult();
  },
  methods: {
    async loadResult() {
      const q = this.$route.query;
      const db = q.db || this.$store.state.userData.db;
      const cID = q.cID || this.$store.state.userData.cId;
      const classID = q.classID;
      const sectionID = q.sectionID;
      const sessionID = q.sessionID;

      if (!classID || !sectionID || !this.examTypeIDs.length || !sessionID) {
        this.error = this.$t(
          "Missing report filters. Please open this report from Print Result."
        );
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await axios.post(
          `${this.$store.state.domain}Marks/ViewResult`,
          {
            campusID: Number(cID),
            clsID: Number(classID),
            sec: Number(sectionID),
            examTypeID: this.examTypeIDs,
            sessionID: Number(sessionID),
            dtFrom: null,
            dtTo: null,
            absentDeduction: 0,
            sIDs: this.requestedSIDs.map((id) => Number(id)),
          },
          {
            params: { db },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              "Content-Type": "application/json",
              accept: "application/json",
            },
          }
        );

        const payload =
          response.data && response.data.data ? response.data.data : {};

        if (payload.campus) {
          this.campus = {
            name: payload.campus.name || this.campus.name,
            address: payload.campus.address || "",
            logo: payload.campus.logo || null,
          };
        }

        if (payload.meta) {
          this.meta = {
            className: payload.meta.className || q.clsName || "",
            sectionName: payload.meta.sectionName || q.secName || "",
            sessionName: payload.meta.sessionName || q.sessionName || "",
            examTitle: payload.meta.examTitle || q.examTypeName || "",
          };
        }

        const rows = Array.isArray(payload.students) ? payload.students : [];
        this.students = rows
          .filter((row) => {
            if (!this.requestedSIDs.length) return true;
            const sID = row.student && row.student.id;
            return this.requestedSIDs.includes(String(sID));
          })
          .map((row) => this.normalizeStudent(row));
      } catch (e) {
        console.error("Farabi result load error:", e);
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          this.$t("Failed to load report.");
      } finally {
        this.loading = false;
      }
    },

    normalizeStudent(row) {
      const student = row.student || {};
      const result = row.result || {};
      const marks = (Array.isArray(row.marks) ? row.marks : []).map((mark) => {
        const total = Number(mark.total);
        const obtained = Number(
          mark.originalObtained !== undefined && mark.originalObtained !== null
            ? mark.originalObtained
            : mark.obtained
        );
        let percentage = mark.meta_subj_percenatge;
        if (percentage === undefined || percentage === null) {
          percentage =
            Number.isFinite(total) && total > 0 && Number.isFinite(obtained)
              ? (obtained / total) * 100
              : 0;
        }
        return {
          subjectName: mark.subjectName || "",
          total: mark.total,
          obtained,
          percentage,
          grade: mark.meta_sub_grade || mark.grade || "",
          status: mark.status || "",
          attendance: String(mark.attendance || "").toUpperCase(),
        };
      });

      const totalMarks =
        result.totalMarks !== undefined && result.totalMarks !== null
          ? result.totalMarks
          : marks.reduce((sum, mark) => sum + (Number(mark.total) || 0), 0);
      const obtainedMarks =
        result.obtainedMarks !== undefined && result.obtainedMarks !== null
          ? result.obtainedMarks
          : marks.reduce((sum, mark) => sum + (Number(mark.obtained) || 0), 0);
      let percentage = result.percentage;
      if (percentage === undefined || percentage === null) {
        percentage =
          Number(totalMarks) > 0
            ? (Number(obtainedMarks) / Number(totalMarks)) * 100
            : 0;
      }

      return {
        student: {
          id: student.id,
          name: student.name || "",
          grNo: student.grNo || "",
          picture: student.picture || null,
        },
        result: {
          totalMarks,
          obtainedMarks,
          percentage,
          grade: result.grade || "",
          rank: result.rank || "",
          result: result.result || "",
        },
        marks,
      };
    },

    formatMark(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (!Number.isFinite(num)) return String(value);
      return Number.isInteger(num) ? String(num) : num.toFixed(2);
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (!Number.isFinite(num)) return String(value);
      return `${num.toFixed(2)}%`;
    },

    attendanceClass(status) {
      const value = String(status || "").toUpperCase();
      if (value === "A") return "obtained-cell--absent";
      if (value === "L") return "obtained-cell--leave";
      return "";
    },

    showStatusLetter(mark) {
      const status = String(mark.attendance || "").toUpperCase();
      if (status !== "A" && status !== "L") return false;
      const obtained = Number(mark.obtained);
      return !Number.isFinite(obtained) || obtained === 0;
    },

    attendanceLabel(status) {
      const value = String(status || "").toUpperCase();
      if (value === "A") return this.$t("Absent");
      if (value === "L") return this.$t("Leave");
      if (value === "P") return this.$t("Present");
      return "";
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.farabi-result {
  min-height: 100vh;
  padding: 24px;
  background: #f1f3f5;
}

.control-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 820px;
  margin: 0 auto 20px;
  padding: 14px 18px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border: none;
  border-radius: 7px;
  font-weight: 600;
  cursor: pointer;
}

.btn-print {
  color: #fff;
  background: #7367f0;
}

.btn-close {
  color: #5e5873;
  background: #e9ecef;
}

.state-panel {
  max-width: 820px;
  margin: 30px auto;
  padding: 40px 20px;
  text-align: center;
  background: #fff;
  border-radius: 10px;

  &__title {
    margin-bottom: 4px;
    font-weight: 700;
  }

  &__text {
    margin: 0;
    color: #6e6b7b;
  }
}

.report-stack {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
}

.report-sheet {
  width: 210mm;
  min-height: 297mm;
  background: #fff;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.15);

  &__frame {
    min-height: 297mm;
    box-sizing: border-box;
  }

  &__body {
    display: flex;
    flex-direction: column;
    min-height: calc(297mm - 2px);
    padding: 6mm 10mm 8mm;
    box-sizing: border-box;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 15px;
    color: #000;
  }
}

.letterhead {
  text-align: center;

  &__logo {
    width: 60px;
    height: 60px;
    object-fit: contain;
    margin-bottom: 4px;
  }

  &__school {
    margin: 0;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 30px;
    font-weight: 700;
    color: #8b0000;
    line-height: 1.2;
  }

  &__address {
    margin: 4px 0 0;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 14px;
    line-height: 1.3;
  }

  &__exam {
    margin: 6px 0 0;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.35;
  }

  &__session {
    margin: 1px 0 12px;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.35;
  }
}

.student-info {
  width: 100%;
  border-collapse: collapse;

  &__details {
    vertical-align: top;
    padding: 6px 0 10px;
  }

  &__table {
    border-collapse: collapse;

    td {
      padding: 2px 0;
      font-size: 15px;
      vertical-align: top;
    }
  }

  &__label {
    width: 65px;
    font-weight: 700;
    white-space: nowrap;
  }

  &__value {
    text-transform: uppercase;
  }

  &__photo {
    width: 90px;
    padding: 0 0 0 10px;
    vertical-align: top;
  }

  &__img,
  &__placeholder {
    display: block;
    width: 80px;
    height: 80px;
    border: 1px solid #000;
    object-fit: cover;
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000;
    background: #f5f5f5;
  }
}

.report-block {
  width: 100%;
  margin-top: 10px;

  &__title {
    padding: 5px 8px;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    background: #d9d9d9;
    border: 1px solid #000;
    border-bottom: none;
    box-sizing: border-box;
  }
}

table.marks-table,
table.result-table,
table.grade-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;

  th,
  td {
    padding: 5px 6px;
    text-align: center;
    border: 1px solid #000;
  }

  th {
    font-weight: 700;
    background: #d9d9d9;
  }
}

table.marks-table {
  th:first-child,
  td:first-child {
    text-align: left;
    font-weight: 700;
  }
}

.obtained-cell {
  font-weight: 400;
  color: #000;

  &--absent {
    color: #dc2626;
  }

  &--leave {
    color: #ca8a04;
  }
}

.result-cell {
  color: #000;
  font-weight: 700;
}

.report-block--result {
  width: 53%;
}

.report-block--grades {
  width: 41%;
}

.signature {
  width: 210px;
  margin-top: auto;
  margin-bottom: 45mm;
  margin-left: auto;
  margin-right: 12mm;
  text-align: center;

  &__line {
    width: 200px;
    margin: 0 auto;
    border-top: 1px solid #000;
  }

  &__label {
    margin-top: 4px;
    font-size: 14px;
  }
}

@media print {
  html,
  body,
  .farabi-result,
  .report-sheet,
  .report-sheet__frame,
  .report-sheet__body,
  table,
  th,
  td,
  div {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .no-print {
    display: none !important;
  }

  .farabi-result {
    padding: 0 !important;
    background: #fff !important;
    min-height: auto !important;
  }

  .report-stack {
    display: block !important;
    width: 100% !important;
  }

  .report-sheet {
    width: 100% !important;
    min-height: calc(297mm - 12mm) !important;
    margin: 0 !important;
    box-shadow: none !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    page-break-after: always;
    break-after: page;

    &:last-child {
      page-break-after: auto;
      break-after: auto;
    }

    &__frame {
      width: 100% !important;
      min-height: calc(297mm - 12mm) !important;
      border: none !important;
      box-sizing: border-box !important;
    }

    &__body {
      min-height: calc(297mm - 12mm - 2px) !important;
      box-sizing: border-box !important;
    }
  }

  .marks-table tr,
  .result-table,
  .grade-table,
  .signature {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 6mm;
}

@media print {
  html,
  body,
  #app {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
}
</style>
